/**
 * NeuNexus multilingual context (DE/EN/PT).
 *
 * Exports:
 *   T                    — translation dictionaries
 *   LangCtx, useT        — context + hook
 *   LangProvider         — wrap the app once, near the router root
 *   LanguageSwitcher     — fixed pill DE/EN/PT top-right
 */
import React from "react";

// palette required by the LanguageSwitcher below
const SP = {
  bg:     "#080B12",
  border: "rgba(255,255,255,0.08)",
  silver: "#C0C7CE",
  green:  "#22C55E",
};

const T = {
  de: {
    hero: {
      badge: "Industrielle Innovationsplattform · Deutschland",
      tagline: "Nachhaltige industrielle Ökosysteme für die Zukunft aufbauen.",
      description: "Eine deutsche Plattform, die erneuerbare Energie, digitale Intelligenz und industrielle Innovation verbindet. NeuNexus integriert strategische Partner, Forschungseinrichtungen und Technologieanbieter in ein kollaboratives Ökosystem.",
      ctaExplore: "Ökosystem entdecken",
      ctaNeuGreen: "NeuGreen Energy",
      ctaPartners: "Strategische Partner",
    },
    vision: {
      eyebrow: "Ökosystem-Vision",
      title: "Eine Plattform. Vier verbundene Ebenen.",
      subtitle: "NeuNexus als industrieller Integrator · NeuGreen Energy als erste operative Division · strategische Technologiepartner in Digitalisierung, Präzision und Robotik · ein klarer Fahrplan zur Smart Factory der Zukunft.",
      overview: "Strategische Ökosystem-Übersicht",
      overviewSub: "Nachhaltige Energie · Digitale Intelligenz · Präzisionsfertigung · Zukünftige Smart Factory — verankert in Niedersachsen, vernetzt in ganz Europa.",
      arcTitle: "Vier Horizonte des NeuNexus-Ökosystems",
      arcAnchor: "Verankert in Niedersachsen · vernetzt in ganz Europa",
      arcReadMore: "Kapitel lesen",
      arc: [
        { title: "Nachhaltige Energie",      desc: "Photovoltaik, BESS und Private-Label-Versorgung durch NeuGreen Energy.",  status: "Aktiv" },
        { title: "Digitale Intelligenz",     desc: "Digital Twin, industrielle KI und Edge Computing für den Fabrikbetrieb.", status: "Im Aufbau" },
        { title: "Präzisionsfertigung",      desc: "Machine Vision, Metrologie und kognitive Robotik in autonomen Zellen.",  status: "Vorgeschlagen" },
        { title: "Zukünftige Smart Factory", desc: "Der konvergente Demonstrator in Uetze — repliziert über ganz Europa.",   status: "Vision" },
      ],
    },
    dashboard: {
      eyebrow: "Executive Dashboard",
      title: "Status des Ökosystems.",
      subtitle: "Live-Momentaufnahme der industriellen NeuNexus-Plattform — strategische Partner, Technologiebereiche, Geschäftsbereiche und geografische Präsenz.",
      kpi: {
        partners:    { label: "Strategische Partner",   value: "08+",       sub: "Aktive + geplante Kooperationen" },
        techAreas:   { label: "Technologiebereiche",    value: "05",        sub: "Digitalisierung · Vision · Erneuerbar · Robotik · Forschung" },
        divisions:   { label: "Geschäftsbereiche",      value: "01+",       sub: "NeuGreen Energy operativ" },
        stage:       { label: "Entwicklungsstufe",      value: "Fundament", sub: "Industrielle Fundament-Phase" },
        location:    { label: "Standort",               value: "Deutschland", sub: "Niedersachsen · EU" },
      },
    },
    map: {
      eyebrow: "NeuNexus Ökosystem",
      title: "Die industrielle Hierarchie.",
      subtitle: "Von der strategischen Plattform bis zu operativen Produkten — ein integrierter Stack für erneuerbare Energie, industrielle Digitalisierung und autonome Fertigung.",
      nodes: {
        neunexus:  "Industrielle Innovationsplattform",
        neugreen:  "Bereich Nachhaltige Energie",
        solar:     "Solar",
        storage:   "Speicher",
        digital:   "Digitale Energie",
        digitalTwin: "Digital Twin",
        edge:      "Edge KI",
        precision: "Präzisionsvision",
        smart:     "Smart Factory Vision",
        smartSub:  "Erneuerbar · Intelligent · Autonom",
      },
    },
    neugreen: {
      eyebrow: "Erste Geschäftsdivision",
      title: "NeuGreen Energy.",
      tagline: "Private-Label-Plattform für nachhaltige Energie.",
      description: "NeuGreen arbeitet mit internationalen Herstellern zusammen, um erneuerbare Energielösungen unter eigener Marke zu entwickeln — Solarerzeugung, Batteriespeicherung, Hybridsysteme und intelligentes Energiemanagement, als schlüsselfertige Lösungen für den deutschen und europäischen Markt.",
      cta: "NeuGreen Präsentation öffnen",
      products: {
        solar:   { title: "HX Solar",             tag: "Photovoltaik-Systeme", bullets: ["N-Type TOPCon Solarmodule", "Wohn- & Gewerbebereich", "Private-Label-Kits"] },
        storage: { title: "HX Storage",           tag: "Batteriesysteme",      bullets: ["LFP Energiespeicher 5–500 kWh", "Hybridlösungen", "Kommerzielle BESS"] },
        mgmt:    { title: "HX Energy Management", tag: "Monitoring & KI",      bullets: ["Live-Monitoring", "Lastoptimierung", "KI-Integration bereit"] },
      },
    },
    partners: {
      eyebrow: "Strategisches Partner Center",
      title: "Das industrielle Netzwerk.",
      subtitle: "NeuNexus wächst durch Partnerschaften. Unten die vorgeschlagenen Hersteller und Technologieanbieter, mit denen wir Kooperationen für den nächsten Horizont sondieren.",
      activeLabel: "NeuGreen Energy · Vorgeschlagene Zusammenarbeit",
      futureLabel: "Technologiepartner · Sondierung / Zusammenarbeit",
      statuses: {
        active:   "Aktiver Partner",
        proposed: "Vorgeschlagene Zusammenarbeit",
        research: "Vorgeschlagene Forschungskooperation",
      },
      roles: {
        bluesun:    "Solartechnologie-Partner",
        wattzing:   "Energiespeicher-Partner",
        beny:       "Energieinfrastruktur-Partner",
        siemens:    "Industrielle Digitalisierung",
        vcow:       "Industrial Edge · KI-Compute",
        dfoptix:    "Präzisionsvision & Messtechnik",
        fraunhofer: "Angewandte Forschung",
        neura:      "Autonome Fertigung",
      },
    },
    roadmap: {
      eyebrow: "Industrieller Fahrplan",
      title: "Vom Produkt zur Plattform.",
      subtitle: "Vier Horizonte — beginnend mit Private-Label-Energie heute, hin zu einem vollständig digitalen, autonomen industriellen Ökosystem.",
      now: "Jetzt",
      phases: [
        { title: "NeuGreen Private Label Start",     bullets: ["Produktentwicklung", "OEM-Partnerschaften", "Markteintritt", "Technische Dokumentation"] },
        { title: "Lokaler Kompetenzaufbau",           bullets: ["Trainingsprogramme", "Ingenieur-Zertifizierung", "Installationsnetzwerk", "Technischer Support"] },
        { title: "Industrielle Digitalisierung",      bullets: ["Digital-Twin-Einführung", "KI-Systemintegration", "Automatisierungspipelines", "Edge Computing"] },
        { title: "NeuNexus Smart Factory",            bullets: ["Erneuerbare Energie", "Intelligente Produktion", "Autonome Systeme", "Industrielle KI"] },
      ],
    },
    transfer: {
      eyebrow: "Technologietransfer",
      title: "Lokales Fachwissen aufbauen.",
      description: "Unsere Technologiepartner liefern nicht nur Hardware. Sie unterstützen NeuNexus aktiv durch strukturierten Wissenstransfer — von der Ingenieur-Zertifizierung bis zu Online-Curricula und Vor-Ort-Coaching. Das Ziel: nachhaltige, autonome industrielle Expertise innerhalb unseres eigenen Netzwerks.",
      pills: ["Ingenieur-Training", "Technische Workshops", "Online-Ausbildung", "Installations-Support", "Wissenstransfer", "Zertifizierungsprogramme"],
    },
    germany: {
      eyebrow: "Warum Deutschland",
      title: "Entwickelt in Niedersachsen.",
      description: "Deutschland liefert die industrielle DNA, technische Tiefe und Forschungsnetzwerke, die ökosystem-skalige Plattformen möglich machen. NeuNexus wird hier gebaut — in einem multikulturellen, internationalen Umfeld, in dem verschiedene Kulturen, Unternehmen und Märkte natürlich kooperieren.",
      location: "Uetze · Niedersachsen · Deutschland",
      chips: [
        { label: "Industrielle Exzellenz",     sub: "Weltweit führende Fertigungstradition" },
        { label: "Ingenieurwissen",             sub: "Tiefe Bank an technischen Talenten" },
        { label: "Forschungskapazität",         sub: "Fraunhofer + Universitätspartnerschaften" },
        { label: "Innovationsnetzwerke",        sub: "Branchenübergreifende Konsortien und EU-Förderung" },
        { label: "Internationales Umfeld",      sub: "Multikulturelle Belegschaft und Märkte" },
        { label: "Energiewende",                sub: "Ambitionierte erneuerbare + industrielle Politik" },
      ],
    },
    cta: {
      assistant: { eyebrow: "HX Assistant · RAG", title: "Fragen Sie das Ökosystem alles.", desc: "Retrieval-Augmented Assistant, verankert in jedem Katalog, Zertifikat und Datenblatt der Plattform. Antworten zitieren die genaue Quelle — auf PT, EN und DE.", cta: "Operations Center öffnen" },
      docs:      { eyebrow: "Document Center",   title: "Die einzige Quelle der Wahrheit.",       desc: "Produktkataloge, technische Datenblätter, Zertifizierungen, Partnerdokumente, Präsentationen und Forschungsreferenzen — sicher gespeichert, durchsuchbar und zitiert.", cta: "Document Center betreten" },
    },
    blueprint: {
      eyebrow: "Strategische Vision",
      title: "Der Bauplan für autonome Fertigung.",
      subtitle: "Das strategische Blueprint-Dokument, das den Weg von NeuNexus zur intelligenten, autonomen Fertigung definiert — kognitive Robotik, humanoide Automatisierung und industrielle KI, integriert in eine einzige Plattform.",
      pill1: "15 Seiten · Executive Deck",
      pill2: "Kognitive Robotik · Humanoide · Industrie 4.0",
      pill3: "Vorgeschlagen: NEURA Robotics · Fraunhofer",
      cta: "Executive Report öffnen",
      note: "Executive Strategic Report · Inhaltsverzeichnis · Anhänge · Druck/PDF verfügbar",
    },
    search: {
      placeholder: "Partner, Kategorie oder Land suchen…",
      noResults: "Keine Ergebnisse für",
      open: "Profil öffnen",
      hint: "Suche",
    },
    bottom: {
      eyebrow: "Der Weg",
      lines: [
        { color: "blue",   text: "NeuNexus entwickelt das industrielle Ökosystem." },
        { color: "green",  text: "NeuGreen Energy schafft das nachhaltige Energiefundament." },
        { color: "silver", text: "Gemeinsam mit Technologiepartnern und Forschungseinrichtungen" },
        { color: "white",  text: "bauen wir den Weg zu intelligenter und autonomer Industrie." },
      ],
    },
    footer: {
      line1: "NeuNexus · Industrielle Innovationsplattform · Deutschland",
      line2: "© 2026 · Integriert & Betrieben von BSGOTECH · Uetze · Niedersachsen",
    },
    partners_page: {
      backHome:      "NeuNexus",
      eyebrow:       "Strategisches Partner Center",
      title:         "Das industrielle Netzwerk.",
      subtitle:      "Jeder strategische Partner der NeuNexus-Plattform — von aktiven NeuGreen-Zulieferern bis zu vorgeschlagenen Kooperationen mit Technologieführern und Forschungseinrichtungen.",
      backCenter:    "Partner Center",
      contribution:  "Beitrag",
      category:      "Kategorie",
      docs:          "Technische Dokumente",
      docsEmpty:     "Noch keine technischen Dokumente verknüpft. Über den HX Assistant hochgeladene Dokumente erscheinen hier automatisch.",
      assistantEyebrow: "HX Assistant · RAG",
      assistantTitle: "Über {name}-Dokumente fragen.",
      assistantDesc: "Datenblätter, Zertifizierungen und Kataloge in PT · EN · DE mit Quellenangaben abfragen.",
      assistantCta:  "Assistant öffnen",
      relatedIn:     "Auch in {group}",
      contact:       "Kontakt",
      contactEmpty:  "Kontaktdaten werden nach Formalisierung veröffentlicht.",
      country:       "Land",
      documents:     "Dokumente",
      strategicGroup: "Strategische Gruppe",
      docsLinked:    "verknüpft",
      engagement:    "Engagement",
      engagementProposed: "Derzeit unter strategischer Bewertung. NeuNexus führt exploratorische Gespräche, um Umfang und gegenseitigen Wert zu definieren.",
      engagementActive:   "Aktive Zusammenarbeit unter NeuGreen Energy. Technischer Dokumentenfluss und gemeinsame Markteinführung etabliert.",
      ecosystemRole: "Ökosystem-Rolle",
      ecosystemRoleDesc: "Teil der {group}-Ebene innerhalb der NeuNexus Industrial Innovation Platform — verbindet die Technologie von {name} mit dem operativen Bereich NeuGreen.",
      notFound:      "Partner nicht gefunden",
      notFoundCta:   "Zurück zum Partner Center",
    },
  },
  en: {
    hero: {
      badge: "Industrial Innovation Platform · Germany",
      tagline: "Building sustainable industrial ecosystems for the future.",
      description: "A Germany-based platform connecting renewable energy, digital intelligence and industrial innovation. NeuNexus integrates strategic partners, research institutions and technology providers into one collaborative ecosystem.",
      ctaExplore: "Explore Ecosystem",
      ctaNeuGreen: "NeuGreen Energy",
      ctaPartners: "Strategic Partners",
    },
    vision: {
      eyebrow: "Ecosystem Vision",
      title: "One platform. Four connected layers.",
      subtitle: "NeuNexus as the industrial integrator · NeuGreen Energy as the first operational division · strategic technology partners across digitalisation, precision and robotics · a clear roadmap to the Smart Factory of the future.",
      overview: "Strategic Ecosystem Overview",
      overviewSub: "Sustainable Energy · Digital Intelligence · Precision Manufacturing · Future Smart Factory — anchored in Lower Saxony, connected across Europe.",
      arcTitle: "Four horizons of the NeuNexus ecosystem",
      arcAnchor: "Anchored in Lower Saxony · connected across Europe",
      arcReadMore: "Read the chapter",
      arc: [
        { title: "Sustainable Energy",      desc: "Photovoltaic, BESS and private-label supply through NeuGreen Energy.",     status: "Active" },
        { title: "Digital Intelligence",    desc: "Digital twin, industrial AI and edge computing for factory operation.",     status: "Building" },
        { title: "Precision Manufacturing", desc: "Machine vision, metrology and cognitive robotics in autonomous cells.",     status: "Proposed" },
        { title: "Future Smart Factory",    desc: "The converged demonstrator in Uetze — replicated across Europe.",           status: "Vision" },
      ],
    },
    dashboard: {
      eyebrow: "Executive Dashboard",
      title: "Ecosystem status.",
      subtitle: "Live snapshot of the NeuNexus industrial platform — strategic partners, technology domains, business divisions and geographical footprint.",
      kpi: {
        partners:    { label: "Strategic Partners",  value: "08+",        sub: "Active + proposed collaborations" },
        techAreas:   { label: "Technology Areas",    value: "05",         sub: "Digitalisation · Vision · Renewable · Robotics · Research" },
        divisions:   { label: "Business Divisions",  value: "01+",        sub: "NeuGreen Energy operational" },
        stage:       { label: "Development Stage",   value: "Foundation", sub: "Industrial Foundation Phase" },
        location:    { label: "Location",            value: "Germany",    sub: "Lower Saxony · EU" },
      },
    },
    map: {
      eyebrow: "NeuNexus Ecosystem",
      title: "The industrial hierarchy.",
      subtitle: "From strategic platform to operational products — one integrated stack for renewable energy, industrial digitalisation and autonomous manufacturing.",
      nodes: {
        neunexus:  "Industrial Innovation Platform",
        neugreen:  "Sustainable Energy Division",
        solar:     "Solar",
        storage:   "Storage",
        digital:   "Digital Energy",
        digitalTwin: "Digital Twin",
        edge:      "Edge AI",
        precision: "Precision Vision",
        smart:     "Smart Factory Vision",
        smartSub:  "Renewable · Intelligent · Autonomous",
      },
    },
    neugreen: {
      eyebrow: "First Business Division",
      title: "NeuGreen Energy.",
      tagline: "Private Label Sustainable Energy Platform.",
      description: "NeuGreen works with international manufacturers to develop renewable energy solutions under its own brand — solar generation, battery storage, hybrid systems and intelligent energy management, delivered as integrated turnkey solutions for the German and European markets.",
      cta: "Open NeuGreen Deck",
      products: {
        solar:   { title: "HX Solar",             tag: "Photovoltaic Systems", bullets: ["N-Type TOPCon solar modules", "Residential & commercial", "Private-label kits"] },
        storage: { title: "HX Storage",           tag: "Battery Systems",       bullets: ["LFP energy storage 5–500 kWh", "Hybrid solutions", "Commercial BESS"] },
        mgmt:    { title: "HX Energy Management", tag: "Monitoring & AI",       bullets: ["Live monitoring", "Load optimisation", "AI integration ready"] },
      },
    },
    partners: {
      eyebrow: "Strategic Partner Center",
      title: "The industrial network.",
      subtitle: "NeuNexus grows through partnerships. Below, the proposed manufacturers and technology providers we are exploring collaborations with for the next horizon.",
      activeLabel: "NeuGreen Energy · Proposed Collaboration",
      futureLabel: "Technology Partners · Exploration / Collaboration",
      statuses: {
        active:   "Active Partner",
        proposed: "Proposed Collaboration",
        research: "Proposed Research Collaboration",
      },
      roles: {
        bluesun:    "Solar Technology Partner",
        wattzing:   "Energy Storage Partner",
        beny:       "Energy Infrastructure Partner",
        siemens:    "Industrial Digitalisation",
        vcow:       "Industrial Edge · AI Compute",
        dfoptix:    "Precision Vision & Metrology",
        fraunhofer: "Applied Research",
        neura:      "Autonomous Manufacturing",
      },
    },
    roadmap: {
      eyebrow: "Industrial Roadmap",
      title: "From product to platform.",
      subtitle: "Four horizons — starting with private-label energy today, building towards a fully digital, autonomous industrial ecosystem.",
      now: "Now",
      phases: [
        { title: "NeuGreen Private Label Launch",    bullets: ["Product development", "OEM partnerships", "Market entry", "Technical documentation"] },
        { title: "Local Capability Development",      bullets: ["Training programmes", "Engineer certification", "Installation network", "Technical support"] },
        { title: "Industrial Digitalisation",         bullets: ["Digital Twin adoption", "AI systems integration", "Automation pipelines", "Edge computing"] },
        { title: "NeuNexus Smart Factory",            bullets: ["Renewable energy", "Intelligent production", "Autonomous systems", "Industrial AI"] },
      ],
    },
    transfer: {
      eyebrow: "Technology Transfer",
      title: "Building local expertise.",
      description: "Our technology partners do not simply deliver hardware. They actively support NeuNexus through structured knowledge transfer — from engineer certification to online curricula and on-site installation coaching. The goal: create sustainable, autonomous industrial expertise within our own network.",
      pills: ["Engineer training", "Technical workshops", "Online education", "Installation support", "Knowledge transfer", "Certification programmes"],
    },
    germany: {
      eyebrow: "Why Germany",
      title: "Engineered in Lower Saxony.",
      description: "Germany provides the industrial DNA, engineering depth and research networks that make ecosystem-scale platforms possible. NeuNexus is built here — in a multicultural, international environment where different cultures, companies and markets naturally cooperate.",
      location: "Uetze · Lower Saxony · Germany",
      chips: [
        { label: "Industrial Excellence",     sub: "World-leading manufacturing tradition" },
        { label: "Engineering Expertise",     sub: "Deep bench of technical talent" },
        { label: "Research Capability",       sub: "Fraunhofer + university partnerships" },
        { label: "Innovation Networks",       sub: "Cross-industry consortia and EU funding" },
        { label: "International Environment", sub: "Multicultural workforce and markets" },
        { label: "Energy Transition",         sub: "Ambitious renewable + industrial policy" },
      ],
    },
    cta: {
      assistant: { eyebrow: "HX Assistant · RAG", title: "Ask the ecosystem anything.",   desc: "Retrieval-augmented assistant grounded in every catalogue, certificate and datasheet in the platform. Answers cite the exact source — in PT, EN and DE.", cta: "Open Operations Center" },
      docs:      { eyebrow: "Document Center",   title: "The single source of truth.",   desc: "Product catalogues, technical datasheets, certifications, partner documents, presentations and research references — securely stored, searchable and cited.", cta: "Enter Document Center" },
    },
    blueprint: {
      eyebrow: "Strategic Vision",
      title: "The Autonomous Manufacturing Blueprint.",
      subtitle: "The strategic blueprint that defines NeuNexus' path towards intelligent, autonomous manufacturing — cognitive robotics, humanoid automation and industrial AI, converged into a single platform.",
      pill1: "15 pages · Executive deck",
      pill2: "Cognitive Robotics · Humanoids · Industry 4.0",
      pill3: "Proposed: NEURA Robotics · Fraunhofer",
      cta: "Read the Executive Report",
      note: "Executive Strategic Report · Table of contents · Annexes · Print/PDF available",
    },
    search: {
      placeholder: "Search partners, categories or countries…",
      noResults: "No results for",
      open: "Open profile",
      hint: "Search",
    },
    bottom: {
      eyebrow: "The Path",
      lines: [
        { color: "blue",   text: "NeuNexus develops the industrial ecosystem." },
        { color: "green",  text: "NeuGreen Energy creates the sustainable energy foundation." },
        { color: "silver", text: "Together with technology partners and research institutions," },
        { color: "white",  text: "we build the path towards intelligent and autonomous industry." },
      ],
    },
    footer: {
      line1: "NeuNexus · Industrial Innovation Platform · Germany",
      line2: "© 2026 · Integrated & Operated by BSGOTECH · Uetze · Lower Saxony",
    },
    partners_page: {
      backHome:      "NeuNexus",
      eyebrow:       "Strategic Partner Center",
      title:         "The industrial network.",
      subtitle:      "Every strategic partner across the NeuNexus industrial innovation platform — from active NeuGreen suppliers to proposed collaborations with technology leaders and research institutions.",
      backCenter:    "Partner Center",
      contribution:  "Contribution",
      category:      "Category",
      docs:          "Technical documents",
      docsEmpty:     "No technical documents linked yet. Documents uploaded via the HX Assistant will appear here automatically.",
      assistantEyebrow: "HX Assistant · RAG",
      assistantTitle: "Ask about {name} technical documents.",
      assistantDesc: "Query datasheets, certifications and catalogues in PT · EN · DE with source citations.",
      assistantCta:  "Open Assistant",
      relatedIn:     "Also in {group}",
      contact:       "Contact",
      contactEmpty:  "Contact details will be published following formalisation.",
      country:       "Country",
      documents:     "Documents",
      strategicGroup: "Strategic Group",
      docsLinked:    "linked",
      engagement:    "Engagement",
      engagementProposed: "Currently under strategic evaluation. NeuNexus is engaging in exploratory discussions to define scope and mutual value.",
      engagementActive:   "Active collaboration under NeuGreen Energy. Technical documentation flow and joint go-to-market established.",
      ecosystemRole: "Ecosystem role",
      ecosystemRoleDesc: "Part of the {group} layer within the NeuNexus Industrial Innovation Platform — connecting {name} technology to the NeuGreen operational division.",
      notFound:      "Partner not found",
      notFoundCta:   "Back to Partner Center",
    },
  },
  pt: {
    hero: {
      badge: "Plataforma de Inovação Industrial · Alemanha",
      tagline: "A construir ecossistemas industriais sustentáveis para o futuro.",
      description: "Uma plataforma sediada na Alemanha que integra energia renovável, inteligência digital e inovação industrial. A NeuNexus reúne parceiros estratégicos, instituições de investigação e fornecedores tecnológicos num ecossistema colaborativo.",
      ctaExplore: "Explorar Ecossistema",
      ctaNeuGreen: "NeuGreen Energy",
      ctaPartners: "Parceiros Estratégicos",
    },
    vision: {
      eyebrow: "Visão do Ecossistema",
      title: "Uma plataforma. Quatro camadas conectadas.",
      subtitle: "NeuNexus como integrador industrial · NeuGreen Energy como primeira divisão operacional · parceiros tecnológicos estratégicos em digitalização, precisão e robótica · um roadmap claro para a Smart Factory do futuro.",
      overview: "Visão Estratégica do Ecossistema",
      overviewSub: "Energia Sustentável · Inteligência Digital · Fabricação de Precisão · Smart Factory Futura — ancorada na Baixa Saxónia, conectada a toda a Europa.",
      arcTitle: "Quatro horizontes do ecossistema NeuNexus",
      arcAnchor: "Ancorada na Baixa Saxónia · conectada a toda a Europa",
      arcReadMore: "Ler o capítulo",
      arc: [
        { title: "Energia Sustentável",       desc: "Fotovoltaico, BESS e fornecimento private-label através da NeuGreen Energy.", status: "Ativa" },
        { title: "Inteligência Digital",      desc: "Gémeo digital, IA industrial e edge computing para operação fabril.",         status: "Em construção" },
        { title: "Fabricação de Precisão",    desc: "Machine vision, metrologia e robótica cognitiva em células autónomas.",       status: "Proposta" },
        { title: "Smart Factory Futura",      desc: "O demonstrador convergente em Uetze — replicado por toda a Europa.",          status: "Visão" },
      ],
    },
    dashboard: {
      eyebrow: "Painel Executivo",
      title: "Estado do ecossistema.",
      subtitle: "Fotografia em direto da plataforma industrial NeuNexus — parceiros estratégicos, domínios tecnológicos, divisões de negócio e presença geográfica.",
      kpi: {
        partners:    { label: "Parceiros Estratégicos",  value: "08+",        sub: "Colaborações ativas + propostas" },
        techAreas:   { label: "Áreas Tecnológicas",      value: "05",         sub: "Digitalização · Visão · Renovável · Robótica · Investigação" },
        divisions:   { label: "Divisões de Negócio",     value: "01+",        sub: "NeuGreen Energy operacional" },
        stage:       { label: "Fase de Desenvolvimento", value: "Fundação",   sub: "Fase de Fundação Industrial" },
        location:    { label: "Localização",             value: "Alemanha",   sub: "Baixa Saxônia · UE" },
      },
    },
    map: {
      eyebrow: "Ecossistema NeuNexus",
      title: "A hierarquia industrial.",
      subtitle: "Da plataforma estratégica aos produtos operacionais — uma stack integrada para energia renovável, digitalização industrial e fabricação autónoma.",
      nodes: {
        neunexus:  "Plataforma de Inovação Industrial",
        neugreen:  "Divisão de Energia Sustentável",
        solar:     "Solar",
        storage:   "Armazenamento",
        digital:   "Energia Digital",
        digitalTwin: "Digital Twin",
        edge:      "Edge IA",
        precision: "Visão de Precisão",
        smart:     "Visão Smart Factory",
        smartSub:  "Renovável · Inteligente · Autónoma",
      },
    },
    neugreen: {
      eyebrow: "Primeira Divisão de Negócio",
      title: "NeuGreen Energy.",
      tagline: "Plataforma Private Label de Energia Sustentável.",
      description: "A NeuGreen trabalha com fabricantes internacionais para desenvolver soluções de energia renovável sob marca própria — geração solar, armazenamento em bateria, sistemas híbridos e gestão inteligente de energia, entregues como soluções turnkey integradas para os mercados alemão e europeu.",
      cta: "Abrir Deck NeuGreen",
      products: {
        solar:   { title: "HX Solar",             tag: "Sistemas Fotovoltaicos", bullets: ["Módulos solares N-Type TOPCon", "Residencial e comercial", "Kits private-label"] },
        storage: { title: "HX Storage",           tag: "Sistemas de Bateria",   bullets: ["Armazenamento LFP 5–500 kWh", "Soluções híbridas", "BESS comercial"] },
        mgmt:    { title: "HX Energy Management", tag: "Monitorização & IA",    bullets: ["Monitorização em direto", "Otimização de cargas", "Pronto para IA"] },
      },
    },
    partners: {
      eyebrow: "Centro de Parceiros Estratégicos",
      title: "A rede industrial.",
      subtitle: "A NeuNexus cresce através de parcerias. Abaixo, os fabricantes e fornecedores de tecnologia propostos com quem exploramos colaborações para o próximo horizonte.",
      activeLabel: "NeuGreen Energy · Colaboração Proposta",
      futureLabel: "Parceiros Tecnológicos · Exploração / Colaboração",
      statuses: {
        active:   "Parceiro Ativo",
        proposed: "Colaboração Proposta",
        research: "Colaboração de Investigação Proposta",
      },
      roles: {
        bluesun:    "Parceiro de Tecnologia Solar",
        wattzing:   "Parceiro de Armazenamento Energético",
        beny:       "Parceiro de Infraestrutura Energética",
        siemens:    "Digitalização Industrial",
        vcow:       "Edge Industrial · IA Compute",
        dfoptix:    "Visão de Precisão e Metrologia",
        fraunhofer: "Investigação Aplicada",
        neura:      "Fabricação Autónoma",
      },
    },
    roadmap: {
      eyebrow: "Roadmap Industrial",
      title: "Do produto à plataforma.",
      subtitle: "Quatro horizontes — começando com energia private-label hoje, a construir um ecossistema industrial totalmente digital e autónomo.",
      now: "Agora",
      phases: [
        { title: "Lançamento NeuGreen Private Label", bullets: ["Desenvolvimento de produto", "Parcerias OEM", "Entrada no mercado", "Documentação técnica"] },
        { title: "Desenvolvimento de Capacidades Locais", bullets: ["Programas de formação", "Certificação de engenheiros", "Rede de instalação", "Suporte técnico"] },
        { title: "Digitalização Industrial", bullets: ["Adoção Digital Twin", "Integração de sistemas IA", "Pipelines de automação", "Edge Computing"] },
        { title: "NeuNexus Smart Factory", bullets: ["Energia renovável", "Produção inteligente", "Sistemas autónomos", "IA Industrial"] },
      ],
    },
    transfer: {
      eyebrow: "Transferência de Tecnologia",
      title: "A construir expertise local.",
      description: "Os nossos parceiros tecnológicos não se limitam a entregar hardware. Apoiam ativamente a NeuNexus através de transferência estruturada de conhecimento — desde certificação de engenheiros até currículos online e coaching de instalação no local. O objetivo: criar expertise industrial sustentável e autónoma dentro da nossa rede.",
      pills: ["Formação de engenheiros", "Workshops técnicos", "Educação online", "Suporte de instalação", "Transferência de conhecimento", "Programas de certificação"],
    },
    germany: {
      eyebrow: "Porquê Alemanha",
      title: "Engenharia na Baixa Saxônia.",
      description: "A Alemanha oferece o ADN industrial, a profundidade de engenharia e as redes de investigação que tornam possíveis plataformas à escala de ecossistema. A NeuNexus é construída aqui — num ambiente multicultural e internacional onde diferentes culturas, empresas e mercados cooperam naturalmente.",
      location: "Uetze · Baixa Saxônia · Alemanha",
      chips: [
        { label: "Excelência Industrial",       sub: "Tradição manufatureira de topo mundial" },
        { label: "Expertise em Engenharia",     sub: "Base sólida de talento técnico" },
        { label: "Capacidade de Investigação",  sub: "Fraunhofer + parcerias universitárias" },
        { label: "Redes de Inovação",           sub: "Consórcios inter-industriais e financiamento UE" },
        { label: "Ambiente Internacional",      sub: "Força de trabalho e mercados multiculturais" },
        { label: "Transição Energética",        sub: "Política renovável + industrial ambiciosa" },
      ],
    },
    cta: {
      assistant: { eyebrow: "HX Assistant · RAG", title: "Pergunte ao ecossistema qualquer coisa.", desc: "Assistente com retrieval-augmentation ancorado em cada catálogo, certificado e datasheet da plataforma. Respostas com citação exata da fonte — em PT, EN e DE.", cta: "Abrir Operations Center" },
      docs:      { eyebrow: "Centro de Documentos", title: "A fonte única de verdade.",             desc: "Catálogos de produto, datasheets técnicos, certificações, documentos de parceiros, apresentações e referências de investigação — armazenados com segurança, pesquisáveis e citados.", cta: "Entrar no Centro de Documentos" },
    },
    blueprint: {
      eyebrow: "Visão Estratégica",
      title: "O Blueprint da Manufatura Autónoma.",
      subtitle: "O documento estratégico que define o caminho da NeuNexus rumo à manufatura inteligente e autónoma — robótica cognitiva, automação humanoide e IA industrial, convergentes numa única plataforma.",
      pill1: "15 páginas · Deck executivo",
      pill2: "Robótica Cognitiva · Humanoides · Indústria 4.0",
      pill3: "Propostos: NEURA Robotics · Fraunhofer",
      cta: "Ler o Relatório Executivo",
      note: "Executive Strategic Report · Índice · Anexos · Impressão/PDF disponíveis",
    },
    search: {
      placeholder: "Procurar parceiros, categorias ou países…",
      noResults: "Sem resultados para",
      open: "Abrir perfil",
      hint: "Pesquisar",
    },
    bottom: {
      eyebrow: "O Caminho",
      lines: [
        { color: "blue",   text: "A NeuNexus desenvolve o ecossistema industrial." },
        { color: "green",  text: "A NeuGreen Energy cria a fundação de energia sustentável." },
        { color: "silver", text: "Juntamente com parceiros tecnológicos e instituições de investigação," },
        { color: "white",  text: "construímos o caminho para uma indústria inteligente e autónoma." },
      ],
    },
    footer: {
      line1: "NeuNexus · Plataforma de Inovação Industrial · Alemanha",
      line2: "© 2026 · Integrada e Operada pela BSGOTECH · Uetze · Baixa Saxônia",
    },
    partners_page: {
      backHome:      "NeuNexus",
      eyebrow:       "Centro de Parceiros Estratégicos",
      title:         "A rede industrial.",
      subtitle:      "Cada parceiro estratégico da plataforma de inovação industrial NeuNexus — desde fornecedores NeuGreen ativos até colaborações propostas com líderes tecnológicos e instituições de investigação.",
      backCenter:    "Centro de Parceiros",
      contribution:  "Contribuição",
      category:      "Categoria",
      docs:          "Documentos técnicos",
      docsEmpty:     "Ainda sem documentos técnicos associados. Documentos carregados via HX Assistant aparecerão aqui automaticamente.",
      assistantEyebrow: "HX Assistant · RAG",
      assistantTitle: "Pergunte sobre documentos {name}.",
      assistantDesc: "Consulte datasheets, certificações e catálogos em PT · EN · DE com citação da fonte.",
      assistantCta:  "Abrir Assistant",
      relatedIn:     "Também em {group}",
      contact:       "Contacto",
      contactEmpty:  "Os detalhes de contacto serão publicados após formalização.",
      country:       "País",
      documents:     "Documentos",
      strategicGroup: "Grupo Estratégico",
      docsLinked:    "associados",
      engagement:    "Envolvimento",
      engagementProposed: "Atualmente em avaliação estratégica. A NeuNexus está a conduzir conversas exploratórias para definir escopo e valor mútuo.",
      engagementActive:   "Colaboração ativa sob NeuGreen Energy. Fluxo de documentação técnica e go-to-market conjunto estabelecidos.",
      ecosystemRole: "Papel no ecossistema",
      ecosystemRoleDesc: "Parte da camada {group} dentro da NeuNexus Industrial Innovation Platform — a ligar a tecnologia da {name} à divisão operacional NeuGreen.",
      notFound:      "Parceiro não encontrado",
      notFoundCta:   "Voltar ao Centro de Parceiros",
    },
  },
};

// simple language context — persisted to localStorage
const LangCtx = React.createContext({ lang: "de", t: T.de, setLang: () => {} });
const useT = () => React.useContext(LangCtx);

const LANGS = [
  { id: "de", label: "DE" },
  { id: "en", label: "EN" },
  { id: "pt", label: "PT" },
  { id: "cv", label: "KV" },
];

// Kriolu de Santiago (ALUPEC) — currently aliased to PT for pages that
// have not yet been translated. Individual pages (e.g. /cabo-verde) may
// override this by providing their own "cv" entry inside their local D dict.
T.cv = T.pt;

const LanguageSwitcher = () => {
  const { lang, setLang } = useT();
  return (
    <div
      className="fixed top-4 right-4 z-50 inline-flex items-center gap-1 px-1 py-1 rounded-full"
      style={{ background: "rgba(8,11,18,0.72)", border: `1px solid ${SP.border}`, backdropFilter: "blur(12px)" }}
      data-testid="lang-switcher"
    >
      {LANGS.map((l) => (
        <button
          key={l.id}
          onClick={() => setLang(l.id)}
          data-testid={`lang-${l.id}`}
          className="px-3 py-1 rounded-full text-[10.5px] font-semibold tracking-[0.15em] transition"
          style={{
            background: lang === l.id ? SP.green : "transparent",
            color:      lang === l.id ? SP.bg    : SP.silver,
          }}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export { T, LangCtx, useT, LanguageSwitcher };

export function LangProvider({ children }) {
  const [lang, setLangState] = React.useState(() => {
    if (typeof window === "undefined") return "de";
    const stored = window.localStorage.getItem("neunexus_lang");
    if (stored && T[stored]) return stored;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return T[nav] ? nav : "de";
  });
  const setLang = React.useCallback((l) => {
    if (!T[l]) return;
    setLangState(l);
    try { window.localStorage.setItem("neunexus_lang", l); } catch { /* */ }
  }, []);
  const ctx = React.useMemo(() => ({ lang, t: T[lang], setLang }), [lang, setLang]);
  React.useEffect(() => {
    document.documentElement.lang = lang;
    const titles = {
      de: "NeuNexus — Industrielle Innovationsplattform · Deutschland",
      en: "NeuNexus — Industrial Innovation Platform · Germany",
      pt: "NeuNexus — Plataforma de Inovação Industrial · Alemanha",
      cv: "NeuNexus — Plataforma di Inovason Industrial · Alimanha",
    };
    const descs = {
      de: "Nachhaltige industrielle Ökosysteme für die Zukunft aufbauen. Erneuerbare Energie, digitale Intelligenz und industrielle Innovation. Deutschland · Niedersachsen.",
      en: "Building sustainable industrial ecosystems for the future. Renewable energy, digital intelligence and industrial innovation. Germany · Lower Saxony.",
      pt: "A construir ecossistemas industriais sustentáveis para o futuro. Energia renovável, inteligência digital e inovação industrial. Alemanha · Baixa Saxônia.",
      cv: "Ta konstrui ekosistemas industriais sustentabl pa futuru. Enerjia renovabl, inteligensia digital i inovason industrial. Alimanha · Baixa Saxônia.",
    };
    document.title = titles[lang] || titles.de;
    const meta = document.querySelector(`meta[name="description"]`);
    if (meta) meta.setAttribute("content", descs[lang] || descs.de);
  }, [lang]);
  return <LangCtx.Provider value={ctx}>{children}</LangCtx.Provider>;
}
